import "@/index.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AccountProvider } from "@/context/AccountContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { AppShell } from "@/components/layout/AppShell";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Chat from "@/pages/Chat";
import Themenradar from "@/pages/Themenradar";
import Pipeline from "@/pages/Pipeline";
import Freigabe from "@/pages/Freigabe";
import Kalender from "@/pages/Kalender";
import EventRadar from "@/pages/EventRadar";
import CreatorTasks from "@/pages/CreatorTasks";
import Performance from "@/pages/Performance";
import PartnerRadar from "@/pages/PartnerRadar";
import PostingHistory from "@/pages/PostingHistory";
import Dateien from "@/pages/Dateien";
import BrandHub from "@/pages/BrandHub";
import Registrations from "@/pages/Registrations";
import PublicRegistration from "@/pages/PublicRegistration";
import MobileCompanion from "@/pages/MobileCompanion";
import Handbuch from "@/pages/Handbuch";
import Einstellungen from "@/pages/Einstellungen";
import PublicShare from "@/pages/PublicShare";
import DropPage from "@/pages/DropPage";
import DmAutomation from "@/pages/DmAutomation";
import DesignForm from "@/pages/DesignForm";
import DesignRequests from "@/pages/DesignRequests";
import DesignReply from "@/pages/DesignReply";
import Buchen from "@/pages/Buchen";
import BuchungUpload from "@/pages/BuchungUpload";
import Kundenakten from "@/pages/Kundenakten";
import BuchungenAdmin from "@/pages/BuchungenAdmin";
import Abrechnung from "@/pages/Abrechnung";
import { Datenschutz, Datenloeschung, Nutzungsbedingungen } from "@/pages/LegalPages";

function Protected({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  if (user === null)
    return (
      <div className="min-h-screen flex items-center justify-center text-neutral-400">Lade…</div>
    );
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

function NoCreator({ children }) {
  const { user } = useAuth();
  if (user?.role === "creator") return <Navigate to="/" replace />;
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-neutral-400">Lade…</div>;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function AppSplash() {
  const [phase, setPhase] = useState("visible");

  useEffect(() => {
    const reduceMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const fadeTimer = window.setTimeout(() => setPhase("leaving"), reduceMotion ? 300 : 1050);
    const removeTimer = window.setTimeout(() => setPhase("hidden"), reduceMotion ? 500 : 1600);
    return () => {
      window.clearTimeout(fadeTimer);
      window.clearTimeout(removeTimer);
    };
  }, []);

  if (phase === "hidden") return null;
  return (
    <div className={`app-splash ${phase === "leaving" ? "is-leaving" : ""}`} data-testid="app-splash" aria-label="Innovationshafen wird geladen">
      <div className="app-splash-orb app-splash-orb-one" />
      <div className="app-splash-orb app-splash-orb-two" />
      <div className="app-splash-glass">
        <img src="/innovationshafen-logo.png" alt="Innovationshafen" className="app-splash-logo" />
        <div className="app-splash-rule" />
        <p>Redaktionsleitstand</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppSplash />
      <AuthProvider>
        <ThemeProvider>
          <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
          <Route path="/anmeldung/:slug" element={<PublicRegistration />} />
          <Route path="/freigabe/:token" element={<PublicShare />} />
          <Route path="/drop/:token" element={<DropPage />} />
          <Route path="/design-anfrage" element={<DesignForm />} />
          <Route path="/insta-studio/:token" element={<DesignForm />} />
          <Route path="/design-antwort/:token" element={<DesignReply />} />
          <Route path="/buchen" element={<Buchen />} />
          <Route path="/upload/:token" element={<BuchungUpload />} />
          <Route path="/datenschutz" element={<Datenschutz />} />
          <Route path="/datenloeschung" element={<Datenloeschung />} />
          <Route path="/nutzungsbedingungen" element={<Nutzungsbedingungen />} />
          <Route
            element={
              <Protected>
                <AccountProvider>
                  <AppShell />
                </AccountProvider>
              </Protected>
            }
          >
            <Route path="/" element={<Dashboard />} />
            <Route path="/chat" element={<Chat />} />
            <Route path="/themenradar" element={<Themenradar />} />
            <Route path="/event-radar" element={<EventRadar />} />
            <Route path="/anmeldungen/:formId" element={<Registrations />} />
            <Route path="/partner-radar" element={<PartnerRadar />} />
            <Route path="/pipeline" element={<Pipeline />} />
            <Route path="/creator-aufgaben" element={<CreatorTasks />} />
            <Route path="/freigabe" element={<Freigabe />} />
            <Route path="/kalender" element={<Kalender />} />
            <Route path="/performance" element={<Performance />} />
            <Route path="/posting-history" element={<PostingHistory />} />
            <Route path="/dm-automation" element={<NoCreator><DmAutomation /></NoCreator>} />
            <Route path="/kundenakten" element={<NoCreator><Kundenakten /></NoCreator>} />
            <Route path="/buchungen" element={<NoCreator><BuchungenAdmin /></NoCreator>} />
            <Route path="/abrechnung" element={<NoCreator><Abrechnung /></NoCreator>} />
            <Route path="/design-auftraege" element={<DesignRequests />} />
            <Route path="/design-anfrage-stellen" element={<DesignForm embedded />} />
            <Route path="/dateien" element={<Dateien />} />
            <Route path="/brand-hub" element={<BrandHub />} />
            <Route path="/handbuch" element={<Handbuch />} />
            <Route path="/einstellungen" element={<Einstellungen />} />
            <Route path="/companion" element={<MobileCompanion />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </ThemeProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
